import { SearchClient } from 'typesense';
import type { ConfigurationOptions } from 'typesense/lib/Typesense/Configuration';
import type {
  DocumentSchema,
  SearchOptions,
  SearchParams,
} from 'typesense/lib/Typesense/Documents';
import { getAssetsForPage, getSimilarAssets, searchAssets } from './assets';
import { fetchBatchStockVideos } from './batch-stock-videos';
import { getCarousels, getCarouselsMulti } from './get-carousels';
import { getContributor } from './get-contributor';
import { getContributorAssetCount } from './get-contributor-asset-count';
import { getContributorSlugByName } from './get-contributor-slug-by-name';
import { getContributorSlugsByNames } from './get-contributor-slugs-by-names';
import { getSimilarCategories } from './get-similar-categories';
import { getStockFootage } from './get-stock-footage';
import { getStockFootageByCategoryEmbedding } from './get-stock-footage-by-category';
import { getStockFootageZoneSampled } from './get-stock-footage-zone-sampled';
import { getVideoCategoriesByGroup } from './get-video-categories-by-group';
import { getVideoCategory } from './get-video-category';
import { getVideoCategoryGroups } from './get-video-category-groups';
import {
  getMotionGraphicsCategory,
  getPageRedirect,
  getSfx,
  getSfxVariants,
  getTrack,
} from './lookups';
import { fetchLutCategoriesForAsset } from './lut-categories-for-asset';
import {
  getCategoryPreselectedFilters,
  getLutCategoryPreselectedFilters,
} from './parse-category-query';
import { fetchSearchResults } from './search-assets';
import { fetchSimilarMotionGraphics } from './similar-motion-graphics';
import { fetchSimilarStockVideos } from './similar-stock-videos';

// Called when a query fails and the caller gets an empty result instead.
export type SetlistErrorHandler = (
  error: unknown,
  context: { operation: string } & Record<string, unknown>
) => void;

export interface SetlistContext {
  client: SearchClient;
  onError: SetlistErrorHandler;
}

export type SetlistConfig = Pick<
  ConfigurationOptions,
  'apiKey' | 'connectionTimeoutSeconds' | 'nearestNode' | 'nodes'
> & { onError?: SetlistErrorHandler };

type Bound<T> = {
  [K in keyof T]: T[K] extends (
    context: SetlistContext,
    ...args: infer Args
  ) => infer Result
    ? (...args: Args) => Result
    : never;
};

const bind = <T extends Record<string, unknown>>(
  context: SetlistContext,
  queries: T
) =>
  Object.fromEntries(
    Object.entries(queries).map(([name, query]) => [
      name,
      (...args: unknown[]) =>
        (query as (...all: unknown[]) => unknown)(context, ...args),
    ])
  ) as Bound<T>;

// 512-float vectors (embedding_audio is tracks.v4 only). Every search
// excludes them unless it names one in include_fields. Typesense won't union
// `*` with named fields, so a query that needs one lists every field it reads.
const EMBEDDING_FIELDS = [
  'embedding',
  'embedding_full',
  'embedding_audio',
] as const;

type Fields = string | string[] | undefined;

interface FieldParams {
  exclude_fields?: Fields;
  include_fields?: Fields;
}

const listFields = (fields: Fields) =>
  [fields ?? []]
    .flat()
    .join(',')
    .split(',')
    .map((field) => field.trim())
    .filter(Boolean);

const withDefaultExcludedFields = <T extends FieldParams>(
  params: T,
  common: FieldParams = {}
): T => {
  const excluded = listFields(params.exclude_fields ?? common.exclude_fields);
  const named = [
    ...excluded,
    ...listFields(params.include_fields ?? common.include_fields),
  ];
  const missing = EMBEDDING_FIELDS.filter((field) => !named.includes(field));
  if (missing.length === 0) {
    return params;
  }
  return { ...params, exclude_fields: [...excluded, ...missing].join(',') };
};

// Patches the instance (not the prototype) so every query path gets the default.
const excludeDefaultFields = (client: SearchClient) => {
  const { multiSearch } = client;
  const perform = multiSearch.perform.bind(multiSearch);
  multiSearch.perform = ((
    requests: Parameters<typeof perform>[0],
    common?: FieldParams,
    options?: Parameters<typeof perform>[2]
  ) =>
    perform(
      {
        ...requests,
        searches: requests.searches.map((search) =>
          withDefaultExcludedFields(search, common)
        ),
      } as typeof requests,
      common as Parameters<typeof perform>[1],
      options
    )) as typeof perform;

  const patched = new WeakSet<object>();
  const collections = client.collections.bind(client);
  client.collections = ((name: string) => {
    const collection = collections(name);
    const documents = collection.documents();
    if (!patched.has(documents)) {
      patched.add(documents);
      const search = documents.search.bind(documents);
      documents.search = ((params, options) =>
        search(withDefaultExcludedFields(params), options)) as typeof search;
    }
    return collection;
  }) as typeof collections;

  return client;
};

/**
 * Hosts and the key are always passed in: this package is public and runs in
 * browsers, so it never reads env vars itself. The key must be search-only.
 */
export const createSetlist = ({ onError, ...config }: SetlistConfig) => {
  const context: SetlistContext = {
    client: excludeDefaultFields(new SearchClient(config)),
    onError: onError ?? (() => undefined),
  };

  return {
    // For requests that have no named query yet. Prefer a named one.
    multiSearch: context.client.multiSearch.perform.bind(
      context.client.multiSearch
    ) as SearchClient['multiSearch']['perform'],
    searchCollection: <T extends DocumentSchema>(
      collection: string,
      parameters: SearchParams<T>,
      options: SearchOptions = {}
    ) =>
      context.client
        .collections<T>(collection)
        .documents()
        .search(parameters, options),
    // A category's stored query that cannot be parsed goes to onError.
    getCategoryPreselectedFilters: (
      category: Parameters<typeof getCategoryPreselectedFilters>[0]
    ) => getCategoryPreselectedFilters(category, context.onError),
    getLutCategoryPreselectedFilters: (
      category: Parameters<typeof getLutCategoryPreselectedFilters>[0]
    ) => getLutCategoryPreselectedFilters(category, context.onError),
    ...bind(context, {
      fetchBatchStockVideos,
      fetchLutCategoriesForAsset,
      fetchSearchResults,
      fetchSimilarMotionGraphics,
      fetchSimilarStockVideos,
      getAssetsForPage,
      getCarousels,
      getCarouselsMulti,
      getContributor,
      getContributorAssetCount,
      getContributorSlugByName,
      getContributorSlugsByNames,
      getMotionGraphicsCategory,
      getPageRedirect,
      getSfx,
      getSfxVariants,
      getSimilarAssets,
      getSimilarCategories,
      getStockFootage,
      getStockFootageByCategoryEmbedding,
      getStockFootageZoneSampled,
      getTrack,
      getVideoCategoriesByGroup,
      getVideoCategory,
      getVideoCategoryGroups,
      searchAssets,
    }),
  };
};

export type Setlist = ReturnType<typeof createSetlist>;
